"use client";
import React, { useState, useRef } from 'react';
import { MediaBlock, TextBlock, ApplyButtonBlock, IframeWrapper, BenefitsBlock } from './../index';
import {Benefit, BlockBase, ContentData, JobTemplate as JobTemplateInterface, MediaElement} from "../interfaces.ts";
import ContactBlock from "../blocks/client/ContactBlock.tsx";
import HeaderBlock from "../blocks/client/HeaderBlock.tsx";
import {ActiveEditorProvider} from "@inscript/lexical-editor";
import {EditorProvider} from "../context/editorContext.tsx";


type BlockLocation = number[];

interface ContainerProperties {
  layout: number[];
  children: BlockBase<never, never>[][];
}

interface ContainerBlock extends BlockBase<ContainerProperties, never> {
  type: 'container';
}

const Actions = React.forwardRef<HTMLDivElement, {
    block: BlockBase<ContainerProperties|never, never>;
    onBlockDelete?: (block: BlockBase<ContainerProperties|never, never>) => void;
    onBlockSettingsClick?: (block: BlockBase<ContainerProperties|never, never>) => void;
    position: { top: number; left: number; };
    editable?: boolean;
}>(({ block, onBlockDelete, onBlockSettingsClick, position, editable }, ref) => {

    //const handleMouseOverBlock = (e: React.MouseEvent, block: BlockBase<never, never>) => {
    //  e.preventDefault();
    //  setHoveredBlock(block);
    //}
    return (
        <div
          ref={ref}
          style={{ position: 'absolute', top: position.top, left: position.left, zIndex: 10 }}
        >
            {onBlockDelete && (
              <button
                onClick={() => onBlockDelete && onBlockDelete(block)}
                style={{
                  cursor: 'pointer',
                }}
              >
                🗑️
              </button>
            )}
            {onBlockSettingsClick && ((!editable && ['textBlock', 'benefitsBlock', 'applyButtonBlock', 'mediaBlock', 'contactBlock', 'container', 'headerBlock'].includes(block.type)) || (editable && ['mediaBlock'].includes(block.type))) &&
              (
                <button
                  onClick={() => onBlockSettingsClick && onBlockSettingsClick(block)}
                  style={{
                    cursor: 'pointer'
                  }}
                >
                  ⚙️
                </button>
              )
            }
        </div>
    );
});
Actions.displayName = 'Actions';

const EditTemplateRenderer: React.FC<{ fallbackLocale: string, locale: string, template: JobTemplateInterface, onBlockReorder?: (newBlocks: BlockBase<never, never>[]) => void, contentData?: ContentData<any>, editable?: boolean, onBlockSettingsClick?: (block: BlockBase<ContainerProperties|never, never>) => void, onBlockDelete?: (block: BlockBase<ContainerProperties|never, never>) => void, benefits?: Benefit[], isMobile?: boolean, newMediaCallback?: (args: { blockId: string }) => Promise<string>, mediaLibraryCallback?: (args: { blockId: string }) => Promise<string>, dragAndDropMediaCallback?: (args: { blockId: string; file: File }) => Promise<string>, handleBlockSettingSave?: (updatedBlock: BlockBase<never, any>) => void, setContentData?: (data: ContentData<any>) => void, files?: MediaElement[], namespace?: string, primaryColor?: string, useIframe?: boolean, availableLocales?: string[] }> = ({ fallbackLocale, locale, template, onBlockReorder, contentData, editable = false, onBlockSettingsClick, onBlockDelete, benefits, isMobile, newMediaCallback, mediaLibraryCallback, dragAndDropMediaCallback, handleBlockSettingSave, setContentData, files, namespace = 'default', primaryColor = '#3b82f6', useIframe = true, availableLocales = [] }) => {
  const blocks = template.blocks;
  const [draggedItemLocation, setDraggedItemLocation] = useState<BlockLocation | null>(null);
  const [dropTargetLocation, setDropTargetLocation] = useState<BlockLocation | null>(null);
  const [hoveredBlock, setHoveredBlock] = useState<BlockBase<ContainerProperties|never, never> | null>(null);
  const [actionsPosition, setActionsPosition] = useState<{ top: number; left: number; } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null)

  const handleDragStart = (e: React.DragEvent, location: BlockLocation) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', 'block');
    setDraggedItemLocation(location);
  };

  const handleDragOver = (e: React.DragEvent, to: BlockLocation) => {
    e.preventDefault();
    e.stopPropagation();

    const newComponentData = e.dataTransfer.getData('component');
    if (newComponentData) {
        if (null === draggedItemLocation) {
            setDraggedItemLocation([-1]);
        }
    }

    if (JSON.stringify(to) !== JSON.stringify(dropTargetLocation)) {
        setDropTargetLocation(to);
    }
  };

  const handleMouseOverBlock = (e: React.MouseEvent, block: BlockBase<ContainerProperties|never, never>) => {
    e.preventDefault();
    e.stopPropagation();
    setHoveredBlock(block);
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    if (useIframe && iframeRef.current?.contentWindow) {
        setActionsPosition({ top: rect.top + iframeRef.current.contentWindow.scrollY, left: rect.left + iframeRef.current.contentWindow.scrollX });
    } else {
        // Calculate position relative to the nearest positioned ancestor
        const offsetParent = target.offsetParent as HTMLElement | null;
        if (offsetParent) {
            const parentRect = offsetParent.getBoundingClientRect();
            setActionsPosition({
                top: rect.top - parentRect.top,
                left: rect.left - parentRect.left
            });
        } else {
            setActionsPosition({ top: rect.top, left: rect.left });
        }
    }
  };

  const handleMouseLeaveBlock = (e: React.MouseEvent) => {
    // @ts-expect-error didn't have time for type checks here
    if (e.relatedTarget && e.relatedTarget.nodeType && actionsRef.current && actionsRef.current.contains(e.relatedTarget)) {
      // ignore if it points to actions
      return;
    }
    setHoveredBlock(null);
    setActionsPosition(null);
  }

  const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedItemLocation) {
          return;
      }

      const newBlocks = JSON.parse(JSON.stringify(blocks));

      // Extract the moved block
      let movedBlock: BlockBase<never, never> | undefined;
      const sourceIsRoot = draggedItemLocation.length === 1;

      if (sourceIsRoot) {
          const [sourceIndex] = draggedItemLocation;
          [movedBlock] = newBlocks.splice(sourceIndex, 1);
      } else {
          const [sourceContainerIndex, sourceColumnIndex, sourceItemIndex] = draggedItemLocation;
          const sourceContainer = newBlocks[sourceContainerIndex] as ContainerBlock;
          if (sourceContainer?.properties?.children?.[sourceColumnIndex]) {
              [movedBlock] = sourceContainer.properties.children[sourceColumnIndex].splice(sourceItemIndex, 1);
          }
      }

      if (!movedBlock) {
          setDraggedItemLocation(null);
          setDropTargetLocation(null);
          return;
      }

      if (movedBlock.type === 'container' && dropTargetLocation && draggedItemLocation) {
        if (draggedItemLocation.length < dropTargetLocation.length) {
          let isDescendant = true;
          for (let i = 0; i < draggedItemLocation.length; i++) {
            if (draggedItemLocation[i] !== dropTargetLocation[i]) {
              isDescendant = false;
              break;
            }
          }
          if (isDescendant) {
            setDraggedItemLocation(null);
            setDropTargetLocation(null);
            return;
          }
        }
      }

      // Insert the moved block
      const targetIsRoot = dropTargetLocation?.length === 1;

      if (targetIsRoot) {
          let [targetIndex] = dropTargetLocation;
          if (sourceIsRoot && draggedItemLocation[0] < targetIndex) {
              targetIndex--;
          }
          newBlocks.splice(targetIndex, 0, movedBlock);
      } else {
          if (null !== dropTargetLocation) {
              let targetContainerIndex;
              const [rawTargetContainerIndex, targetColumnIndex, targetItemIndex] = dropTargetLocation;
              targetContainerIndex = rawTargetContainerIndex;

              if (sourceIsRoot && draggedItemLocation[0] < targetContainerIndex) {
                  targetContainerIndex--;
              }
              const targetContainer = newBlocks[targetContainerIndex] as ContainerBlock;
              if (targetContainer?.properties?.children?.[targetColumnIndex]) {
                  targetContainer.properties.children[targetColumnIndex].splice(targetItemIndex, 0, movedBlock);
              } else {
                  console.error("Drop failed: target container or column not found.", {
                      targetContainerIndex,
                      targetColumnIndex,
                      newBlocks
                  });
              }
          }
      }

      if (onBlockReorder) {
          onBlockReorder(newBlocks);
      }

      setDraggedItemLocation(null);
      setDropTargetLocation(null);
  };

  const handleDropNew = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const newComponentData = e.dataTransfer.getData('component');
      if (!newComponentData) {
          return;
      }

      const newComponent: BlockBase<never, never> = JSON.parse(newComponentData);
      newComponent.id = `${newComponent.type}-${Date.now()}`;

      const newBlocks = JSON.parse(JSON.stringify(blocks));

      if (null !== dropTargetLocation) {
          const targetIsRoot = dropTargetLocation?.length === 1;

          if (targetIsRoot) {
              const [targetIndex] = dropTargetLocation;
              newBlocks.splice(targetIndex, 0, newComponent);
          } else {
              const [targetContainerIndex, targetColumnIndex, targetItemIndex] = dropTargetLocation;
              const targetContainer = newBlocks[targetContainerIndex] as ContainerBlock;
              if (targetContainer?.properties?.children?.[targetColumnIndex]) {
                  targetContainer.properties.children[targetColumnIndex].splice(targetItemIndex, 0, newComponent);
              } else {
                  console.error("Drop failed: target container or column not found.", {
                      targetContainerIndex,
                      targetColumnIndex,
                      newBlocks
                  });
              }
          }

          if (onBlockReorder) {
              onBlockReorder(newBlocks);
          }
      }

      setDraggedItemLocation(null);
      setDropTargetLocation(null);
  };

  const handleDragLeave = () => {
      setDropTargetLocation(null);
  }

  const handleDragEnd = () => {
      setDraggedItemLocation(null);
      setDropTargetLocation(null);
      setHoveredBlock(null);
      setActionsPosition(null);
  }

  const DropZone: React.FC<{location: BlockLocation}> = ({ location }) => {
      const isDropTarget = dropTargetLocation && JSON.stringify(location) === JSON.stringify(dropTargetLocation);
      const isDraggedItemLocation = draggedItemLocation && JSON.stringify(location) === JSON.stringify(draggedItemLocation);
      const isDraggingNew = draggedItemLocation?.length === 1 && draggedItemLocation[0] === -1;

      const shouldBeVisible = (draggedItemLocation && isDropTarget) || isDraggingNew;

      if (!shouldBeVisible || isDraggedItemLocation) {
          return null;
      }

      return (
          <div
              onDragOver={(e) => handleDragOver(e, location)}
              onDrop={(e) => {
                  const newComponentData = e.dataTransfer.getData('component');
                  if (newComponentData) {
                      handleDropNew(e);
                  } else {
                      handleDrop(e);
                  }
              }}
              onDragLeave={handleDragLeave}
              style={{
                  height: isDropTarget ? '50px' : 0,
                  backgroundColor: 'lightblue',
                  overflow: 'hidden',
                  transition: 'height 0.2s ease-in-out, background-color 0.2s ease-in-out',
                  width: '100%',
              }}
          />
      );
  };

  const EmptyColumnPlaceholder: React.FC<{location: BlockLocation}> = ({location}) => {
      const isDropTarget = dropTargetLocation && JSON.stringify(location) === JSON.stringify(dropTargetLocation);

      return (
          <div
              onDragOver={(e) => handleDragOver(e, location)}
              onDrop={(e) => {
                  const newComponentData = e.dataTransfer.getData('component');
                  if (newComponentData) {
                      handleDropNew(e);
                  } else {
                      handleDrop(e);
                  }
              }}
              onDragLeave={handleDragLeave}
              style={{
                  minHeight: '200px',
                  backgroundColor: isDropTarget ? 'lightblue' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ccc',
                  width: '100%',
                  height: '100%',
              }}
          >
              <span>Drop block here</span>
          </div>
      )
  }

  const renderContainer = (block: BlockBase<any, any>, location: BlockLocation): React.ReactNode => {
    const isContainerBlock = (block: BlockBase<any, any>): block is ContainerBlock => {
      return block.type === 'container';
    };

    if (!isContainerBlock(block)) {
      console.error(`Expected a container block, but got ${block.type}`);
      return null;
    }

    const {id} = block;
    const {children} = block.properties;
    const isBeingDragged = draggedItemLocation && JSON.stringify(location) === JSON.stringify(draggedItemLocation);

    return (
      <div
        key={`${namespace}-${id}`}
        className={id}
        data-container-block={id}
        draggable={!editable}
        onDragStart={(e) => handleDragStart(e, location)}
        onDragEnd={handleDragEnd}
        onDrop={(e) => {
            const newComponentData = e.dataTransfer.getData('component');
            if (newComponentData) {
                handleDropNew(e);
            } else {
                handleDrop(e);
            }
        }}
        onDragOver={(e) => handleDragOver(e, location)}
        onMouseEnter={(e) => handleMouseOverBlock(e, block)}
        onMouseLeave={handleMouseLeaveBlock}
        style={{
          opacity: isBeingDragged ? 0.8 : 1,
          cursor: !editable ? 'move' : 'default',
        }}
      >
        {children.map((column, columnIndex) => (
          <div
            key={`${namespace}-column-${columnIndex}`}
            className={`column-${columnIndex}`}
          >
            {column.length === 0 ? (
              <EmptyColumnPlaceholder location={[...location, columnIndex, 0]} />
            ) : (
              <>
                <DropZone location={[...location, columnIndex, 0]} />
                {column.map((child, childIndex) => (
                    <React.Fragment key={`${namespace}-drop-zone-wrapper-${child.id}`}>
                        {renderComponent(child, [...location, columnIndex, childIndex])}
                        <DropZone location={[...location, columnIndex, childIndex + 1]} />
                    </React.Fragment>
                ))}
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  const renderComponent = (block: BlockBase<never, never>, location: BlockLocation): React.ReactNode => {
    const isBeingDragged = draggedItemLocation && JSON.stringify(location) === JSON.stringify(draggedItemLocation);

    let component;
    switch (block.type) {
        case 'mediaBlock': component = <MediaBlock block={block} contentData={contentData} locale={locale} fallbackLocale={fallbackLocale} newMediaCallback={newMediaCallback} mediaLibraryCallback={mediaLibraryCallback} dragAndDropMediaCallback={dragAndDropMediaCallback} handleBlockSettingSave={handleBlockSettingSave} setContentData={setContentData} files={files} primaryColor={primaryColor} availableLocales={availableLocales} />; break;
        case 'textBlock': component = <TextBlock block={block} contentData={contentData} locale={locale} fallbackLocale={fallbackLocale} />; break;
        case 'applyButtonBlock': component = <ApplyButtonBlock block={block} contentData={contentData} locale={locale} fallbackLocale={fallbackLocale} />; break;
        case 'benefitsBlock': component = <BenefitsBlock benefits={benefits || []} block={block} contentData={contentData} locale={locale} fallbackLocale={fallbackLocale} />; break;
        case 'contactBlock': component = <ContactBlock block={block} contentData={contentData} locale={locale} fallbackLocale={fallbackLocale} />; break;
        case 'headerBlock': component = <HeaderBlock block={block} contentData={contentData} locale={locale} fallbackLocale={fallbackLocale} />; break;
        case 'container': return renderContainer(block, location);
        default: return null;
    }

      return (
        <div
            key={`${namespace}-${block.id}`}
            className={block.type}
            style={{
                opacity: isBeingDragged ? 0.7 : 1,
                cursor: !editable ? 'grab' : 'default',
            }}
            draggable={!editable}
            onDragStart={(e) => handleDragStart(e, location)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, location)}
            onDrop={(e) => {
                const newComponentData = e.dataTransfer.getData('component');
                if (newComponentData) {
                    handleDropNew(e);
                } else {
                    handleDrop(e);
                }
            }}
            onMouseEnter={(e) => handleMouseOverBlock(e, block)}
            onMouseLeave={handleMouseLeaveBlock}
        >
            {React.cloneElement(component as React.ReactElement, {
                // @ts-expect-error editable ignore
                editable: editable
            })}
        </div>
    );
  };

  let styles = `${template.styles}`;
  if (template.files) {
    template.files.forEach(file => {
      styles = styles.replace(`{${file.id}}`, styles)
    })
  }

  return (
    <EditorProvider namespace={namespace}>
      <div style={{ width: isMobile ? '375px' : '100%', transition: 'width 0.3s ease-in-out' }}>
        {useIframe ? (
          <IframeWrapper
              ref={iframeRef}
              styles={styles}
              onDrop={handleDropNew}
          >
            {hoveredBlock && actionsPosition && (
                <Actions
                    ref={actionsRef}
                    block={hoveredBlock}
                    editable={editable}
                    onBlockDelete={onBlockDelete}
                    onBlockSettingsClick={onBlockSettingsClick}
                    position={actionsPosition}
                />
            )}
            <ActiveEditorProvider>
            <div className="layout-container">
            {blocks.length === 0 ? (
              <div
                onDragOver={(e) => handleDragOver(e, [0])}
                onDrop={(e) => {
                  const newComponentData = e.dataTransfer.getData('component');
                  if (newComponentData) {
                    handleDropNew(e);
                  } else {
                    handleDrop(e);
                  }
                }}
                onDragLeave={handleDragLeave}
                style={{
                  minHeight: '200px',
                  backgroundColor: dropTargetLocation && JSON.stringify([0]) === JSON.stringify(dropTargetLocation) ? 'lightblue' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ccc',
                  width: '100%',
                  border: '2px dashed #ccc',
                }}
              >
                <span>Drop block here</span>
              </div>
            ) : (
              <>
                <DropZone location={[0]} />
                {blocks.map((block, index) => (
                  <React.Fragment key={`${namespace}-dropzone1-wrapper-${block.id}`}>
                    {
                      // @ts-expect-error block type
                      renderComponent(block, [index])
                    }
                    <DropZone location={[index + 1]} />
                  </React.Fragment>
                ))}
              </>
            )}
            </div>
          </ActiveEditorProvider>
          </IframeWrapper>
        ) : (
          <div
              onDrop={handleDropNew}
              style={{ position: 'relative' }}
          >
            {styles && <style dangerouslySetInnerHTML={{ __html: styles }} />}
            {hoveredBlock && actionsPosition && (
                <Actions
                    ref={actionsRef}
                    block={hoveredBlock}
                    editable={editable}
                    onBlockDelete={onBlockDelete}
                    onBlockSettingsClick={onBlockSettingsClick}
                    position={actionsPosition}
                />
            )}
            <ActiveEditorProvider>
              <div className="layout-container">
                {blocks.length === 0 ? (
                  <div
                    onDragOver={(e) => handleDragOver(e, [0])}
                    onDrop={(e) => {
                      const newComponentData = e.dataTransfer.getData('component');
                      if (newComponentData) {
                        handleDropNew(e);
                      } else {
                        handleDrop(e);
                      }
                    }}
                    onDragLeave={handleDragLeave}
                    style={{
                      minHeight: '200px',
                      backgroundColor: dropTargetLocation && JSON.stringify([0]) === JSON.stringify(dropTargetLocation) ? 'lightblue' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ccc',
                      width: '100%',
                      border: '2px dashed #ccc',
                    }}
                  >
                    <span>Drop block here</span>
                  </div>
                ) : (
                  <>
                    <DropZone location={[0]} />
                    {blocks.map((block, index) => (
                      <React.Fragment key={`${namespace}-dropzone1-wrapper-${block.id}`}>
                        {
                          // @ts-expect-error block type
                          renderComponent(block, [index])
                        }
                        <DropZone location={[index + 1]} />
                      </React.Fragment>
                    ))}
                  </>
                )}
              </div>
            </ActiveEditorProvider>
          </div>
        )}
      </div>
    </EditorProvider>
  );
};

export default EditTemplateRenderer;

